import React, { useCallback, useMemo } from "react";
import Modal from "../Modal";
import { IoExitOutline } from "react-icons/io5";
import { useAccount } from "@/hooks/useAccount";
import { usePrivy, useUser, useWallets } from "@privy-io/react-auth";
import DisconnectSvg from "../Svg/DisconnectSvg";
import CopySvg from "../Svg/CopySvg";
import { useJwtStorage } from "@/hooks/Storage/useJwtStorage";
import { useProfileStorage } from "@/hooks/Storage/useProfileStorage";
import { Tooltip } from "flowbite-react";
import { BiExport } from "react-icons/bi";
import ExportSvg from "../Svg/Export";
import Button from "../Button";
import { useCopyToClipboard } from "usehooks-ts";
import { toast } from "react-toastify";
import { useProfile } from "@/hooks/meme/useProfile";
import Image from "next/image";
import { TransactionHistory } from "./TransactionHistory";
import clsx from "clsx";
import EditSvg from "@/meme-components/Svg/EditSvg";
import { useEditProfile } from "@/hooks/meme/useEditProfile";
import ModalEditProfile from "@/meme-components/ModalEditProfile/page";
import { useParams } from "next/navigation";
import { teamSelect } from "@/constants/team";
import UserProfileLink from "@/meme-components/UserProfileLink/page";
import CopyCard from "../CopyCard";

interface UserModalProps {
  onDismiss: () => void;
}

export default function UserModal({ onDismiss }: UserModalProps) {
  const {
    showOpenEditProfileModal,
    setShowOpenEditProfileModal,
    fileName,
    setFileName,
    name,
    bio,
    team,
    setTeam,
    isSubmitting,
    hiddenFileInput,
    handleSave,
    errorMessage,
    buttonText,
    handleFileClick,
    handleFileChange,
    handleNameChange,
    handleBioChange,
    handleTeamChange,
    profile: ownerProfile,
    isTeam,
    setIsTeam,
  } = useEditProfile();

  const { authenticated, logout } = usePrivy();

  const { removeJwt } = useJwtStorage();
  const { removeProfile } = useProfileStorage();

  const { address, userWallets } = useAccount();
  const { wallets } = useWallets();
  const { exportWallet } = usePrivy();
  const { user } = useUser();
  const [_, copy] = useCopyToClipboard();
  const { id } = useParams();

  const handleCopy = useCallback(() => {
    if (!address) {
      return;
    }

    copy(address);

    toast.success("Copied to Clipboard", {
      className:
        "!text-bitzy-glass-white !bg-bitzy-v2-black/50 !backdrop-blur !border !border-bitzy-v2-gray/100/25",
      theme: "dark",
      icon: false,
    });
  }, [address, copy]);

  const wrappedOnDismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  const wrappedEditProfileModalOnDismiss = useCallback(() => {
    setShowOpenEditProfileModal(false);
    setTeam(isTeam ? team : undefined);
  }, [isTeam, setShowOpenEditProfileModal, setTeam, team]);

  const handleExport = useCallback(async () => {
    if (authenticated) {
      try {
        wrappedOnDismiss();

        const exportedWallet = await exportWallet();
      } catch (error) {
        console.error("Failed to export wallet:", error);
      }
    }
  }, [authenticated, exportWallet, wrappedOnDismiss]);

  const handleLogout = useCallback(async () => {
    if (authenticated) {
      await logout();
      removeJwt();
      removeProfile();
      wrappedOnDismiss();
    }
  }, [authenticated, logout, removeJwt, removeProfile, wrappedOnDismiss]);

  const walletEmbedded = userWallets?.find(
    (wallet: any) => wallet.connectorType === "embedded"
  );

  const handleOnDetailClick = useCallback(() => {
    setShowOpenEditProfileModal(true);
  }, [setShowOpenEditProfileModal]);

  const imgTeam = useMemo(
    () => teamSelect.find((t) => t.name === team),
    [team]
  );

  const { profile } = useProfile(ownerProfile?.name);

  const displayName = useMemo(() => {
    const name = profile?.name
      ? profile?.name?.startsWith(`0x`)
        ? `${profile?.name.slice(0, 6)}...${profile?.name?.slice(-4)}`
        : profile?.name
      : ownerProfile?.name
      ? `${ownerProfile?.name.slice(0, 6)}...${ownerProfile?.name?.slice(-4)}`
      : "...";

    return (
      <>
        <span className="truncate">{name}</span>
      </>
    );
  }, [profile?.name, ownerProfile?.name]);

  return (
    <>
      <div className="flex flex-col gap-6 overflow-x-auto h-full w-full justify-between items-center">
        <div
          className={
            "w-full grid grid-cols-[90px_auto] justify-center gap-4 items-start"
          }
        >
          <div
            className="relative cursor-pointer pt-2"
            onClick={handleOnDetailClick}
          >
            <div className="w-[90px] h-[90px] border border-bitzy-white overflow-hidden rounded-full relative drop-shadow-[0_3px_3px_rgba(0,0,0,0.25)]">
              <Image
                src={ownerProfile?.profile_picture_url || "/images/bitzy.png"}
                fill
                sizes={"width: 100%"}
                style={{ objectFit: "cover" }}
                alt={ownerProfile?.name || ""}
              />
            </div>

            <div className="w-7 h-7 bg-bitzy-meme-black/50 flex items-center justify-center rounded-lg absolute top-[0px] right-[-3px] z-10">
              <EditSvg className="w-5 h5 fill-transparent stroke-bitzy-white" />
            </div>
          </div>

          <div className="grid w-full gap-1">
            <div
              className={clsx(
                imgTeam ? "grid-cols-[auto_28px]" : "grid-cols-1",
                "grid items-center gap-2 w-fit max-w-full"
              )}
            >
              <h6 className="w-fit max-w-full text-bitzy-white text-base font-semibold truncate drop-shadow-[0_3px_3px_rgba(0,0,0,0.25)]">
                {displayName}
              </h6>
              {imgTeam && (
                <Image
                  src={imgTeam.img}
                  alt={imgTeam.name}
                  width={20}
                  height={20}
                  className="w-7 h-5 shadow-lg rounded"
                />
              )}
            </div>

            <span className="text-bitzy-gray w-full text-sm">
              {ownerProfile?.bio}
            </span>

            <div className="flex gap-2">
              {userWallets?.map((wallet: any, i) => {
                return (
                  <Tooltip
                    key={i}
                    content={wallet.address ?? wallet.email ?? wallet.type}
                    style="auto"
                  >
                    <div
                      className={clsx(
                        "w-6 h-6 group rounded-3xl backdrop-blur inset-shadow flex cursor-pointer justify-center items-center",
                        "border border-bitzy-v2-gray/100/25 bg-bitzy-v2-gray/25 text-bitzy-glass-white inset-shadow"
                      )}
                    >
                      <img
                        src={wallet.icon}
                        alt={wallet.type}
                        className="w-4 h-4 group-hover:opacity-50 transition-all duration-300"
                      />
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        </div>
        <div
          className={clsx(
            walletEmbedded ? "grid-cols-2" : "grid-cols-1",
            "grid grid-rows-2 justify-center gap-3"
          )}
        >
          <CopyCard
            address={address}
            isShort={true}
            isResponsive={false}
            className="!text-base !py-0"
            classNameIcon="w-4 h-4"
          />

          {walletEmbedded && (
            <div
              onClick={handleExport}
              className={clsx(
                "w-full text-center text-bitzy-glass-white group hover:text-bitzy-glass-white/50 flex flex-wrap items-center justify-center gap-2 p-1 px-4 mx-auto cursor-pointer transition-all duration-300",
                "rounded-xl border border-bitzy-v2-gray/100/25 backdrop-blur bg-bitzy-v2-white/25 inset-shadow select-none"
              )}
            >
              <ExportSvg />
              Export Wallet
            </div>
          )}

          <div
            onClick={handleLogout}
            className="col-span-2 row-start-2 text-bitzy-red hover:text-bitzy-red/60 group text-center flex flex-wrap items-center justify-center gap-2 p-1 px-4 mx-auto cursor-pointer transition-all duration-300"
          >
            <DisconnectSvg />
            Disconnect
          </div>
        </div>

        <div className="flex justify-center gap-2">
          <TransactionHistory />
        </div>
      </div>

      {showOpenEditProfileModal && (
        <Modal
          title={`Edit profile`}
          isShow={showOpenEditProfileModal}
          onClose={wrappedEditProfileModalOnDismiss}
        >
          <div className="flex flex-col gap-6 h-full w-full justify-between items-center">
            <ModalEditProfile
              handleSave={handleSave}
              setFileName={setFileName}
              handleFileChange={handleFileChange}
              handleFileClick={handleFileClick}
              isSubmitting={isSubmitting}
              handleNameChange={handleNameChange}
              handleBioChange={handleBioChange}
              name={name}
              bio={bio}
              team={team}
              handleTeamChange={handleTeamChange}
              fileName={fileName}
              hiddenFileInput={hiddenFileInput}
              errorMessage={errorMessage}
              buttonText={buttonText}
              isTeam={isTeam}
              setIsTeam={setIsTeam}
            />
          </div>
        </Modal>
      )}
    </>
  );
}
